import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  ChevronLeft,
  ChevronRight,
  Plus,
  Trash2,
  X,
  Clock,
  Users,
  Wifi,
  WifiOff,
  RefreshCw,
  Save,
  Calendar,
  DoorOpen,
  Edit,
  Zap,
  CheckCircle,
  Copy
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useBusiness } from '../../contexts/BusinessContext';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { ProfilePhoto } from '../../components/ui/ProfilePhoto';
import { db, firebaseConnectionManager } from '../../config/firebase';
import {
  collection,
  doc,
  addDoc,
  updateDoc,
  getDocs,
  query,
  where,
  serverTimestamp,
  getDoc
} from 'firebase/firestore';
import { toast } from 'react-hot-toast';
import { Helmet } from 'react-helmet-async';
import { Loading } from '../../components/ui/Loading';
import { useSystemConfig } from '../../hooks/useSystemConfig';

interface Room {
  id: string;
  name: string;
  capacity: number;
  color: string;
}

interface ScheduledClass {
  id: string;
  name: string;
  instructor: string;
  instructorId: string;
  date: string;
  time: string;
  duration: number;
  maxCapacity: number;
  enrolled: { [userId: string]: boolean };
  classType: string;
  description?: string;
  location: string;
  roomId?: string;
  isActive: boolean;
  isPublished: boolean; // true = visible para usuarios, false = borrador
  scheduledPublicationDate?: any; // Fecha programada para publicación automática
  createdAt: any;
  updatedAt: any;
  businessId: string;
}

interface Instructor {
  id: string;
  firstName: string;
  lastName: string;
  status: 'active' | 'inactive';
  classTypeIds?: string[];
}

interface ClassType {
  id: string;
  name: string;
  description?: string;
  duration: number;
  maxCapacity: number;
  instructorIds?: string[];
  isActive: boolean;
  image?: string;
}

interface User {
  id: string;
  firstName: string;
  lastName: string;
  profilePhotoUrl?: string;
  photoUrl?: string;
}

const ROOM_COLORS = [
  '#3B82F6', '#10B981', '#F59E0B', '#EF4444',
  '#8B5CF6', '#EC4899', '#06B6D4', '#F97316',
];

const DURATION_OPTIONS = [
  { value: 30, label: '30 min' },
  { value: 45, label: '45 min' },
  { value: 60, label: '1 hora' },
  { value: 90, label: '1h 30min' },
  { value: 120, label: '2 horas' },
];

const generateTimeSlots = () => {
  const slots: string[] = [];
  for (let hour = 6; hour < 23; hour++) {
    slots.push(`${hour.toString().padStart(2, '0')}:00`);
    slots.push(`${hour.toString().padStart(2, '0')}:30`);
  }
  slots.push('23:00');
  return slots;
};

const TIME_SLOTS = generateTimeSlots();
const SLOT_HEIGHT = 40;

const timeToMinutes = (time: string): number => {
  const [hours, minutes] = time.split(':').map(Number);
  return hours * 60 + minutes;
};

const getSlotIndex = (time: string): number => {
  const minutes = timeToMinutes(time);
  const startMinutes = timeToMinutes(TIME_SLOTS[0]);
  return Math.floor((minutes - startMinutes) / 30);
};

const getSlotsCount = (duration: number): number => {
  return Math.ceil(duration / 30);
};

const CalendarManagementPage: React.FC = () => {
  const { config: systemConfig } = useSystemConfig();
  const { currentUser } = useAuth();
  const { businessId, currentBusiness } = useBusiness();
  const [currentWeek, setCurrentWeek] = useState(new Date());
  const [classes, setClasses] = useState<ScheduledClass[]>([]);
  const [instructors, setInstructors] = useState<Instructor[]>([]);
  const [classTypes, setClassTypes] = useState<ClassType[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [rooms, setRooms] = useState<Room[]>([]);
  const [selectedRoom, setSelectedRoom] = useState<string>('all');
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showRoomConfig, setShowRoomConfig] = useState(false);
  const [selectedTimeSlot, setSelectedTimeSlot] = useState<{ date: string; time: string } | null>(null);
  const [selectedClass, setSelectedClass] = useState<ScheduledClass | null>(null);
  const [isOnline, setIsOnline] = useState(true);

  // Modo de creación: 'template' (usar clase predefinida) o 'manual'
  const [createMode, setCreateMode] = useState<'template' | 'manual'>('template');
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');

  const [newRoomName, setNewRoomName] = useState('');
  const [newRoomCapacity, setNewRoomCapacity] = useState(20);
  const [editingRoom, setEditingRoom] = useState<Room | null>(null);

  // Estado para programación de publicación
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [scheduleDate, setScheduleDate] = useState('');
  const [scheduleTime, setScheduleTime] = useState('17:00');

  // Estado para clonar semana
  const [showCloneModal, setShowCloneModal] = useState(false);
  const [cloneWeeksCount, setCloneWeeksCount] = useState(1);
  const [isCloning, setIsCloning] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    instructor: '',
    instructorId: '',
    time: '',
    duration: 60,
    maxCapacity: 15,
    classType: '',
    description: '',
    location: 'Sala Principal',
    roomId: ''
  });

  const maxRooms = useMemo(() => {
    const plan = (currentBusiness?.subscription?.plan || 'free') as 'free' | 'basic' | 'pro';
    return systemConfig.plans[plan]?.maxRooms ?? 1;
  }, [currentBusiness, systemConfig]);

  // Helper para formatear fecha en formato local YYYY-MM-DD (sin conversión a UTC)
  const formatLocalDate = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const getWeekDates = (date: Date) => {
    // Crear copia y fijar a mediodía para evitar problemas de zona horaria
    const startOfWeek = new Date(date);
    startOfWeek.setHours(12, 0, 0, 0);

    // Obtener día de la semana (0 = domingo, 1 = lunes, ..., 6 = sábado)
    const day = startOfWeek.getDay();

    // Calcular cuántos días retroceder para llegar al lunes
    // Si es domingo (0), retroceder 6 días; si no, retroceder (day - 1) días
    const daysToMonday = day === 0 ? 6 : day - 1;
    startOfWeek.setDate(startOfWeek.getDate() - daysToMonday);

    const weekDates = [];
    for (let i = 0; i < 7; i++) {
      const currentDate = new Date(startOfWeek);
      currentDate.setDate(startOfWeek.getDate() + i);
      weekDates.push(formatLocalDate(currentDate));
    }
    return weekDates;
  };

  const weekDates = useMemo(() => getWeekDates(currentWeek), [currentWeek]);

  // Evitar que el calendario arranque con scroll horizontal desplazado
  // (algunos navegadores/PWA restauran scrollLeft y “ocultan” el lunes + la columna de hora)
  const unifiedCalendarScrollRef = useRef<HTMLDivElement | null>(null);
  const singleCalendarScrollRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const reset = (el: HTMLDivElement | null) => {
      if (!el) return;
      el.scrollLeft = 0;
      requestAnimationFrame(() => {
        if (el) el.scrollLeft = 0;
      });
    };
    reset(unifiedCalendarScrollRef.current);
    reset(singleCalendarScrollRef.current);
  }, [currentWeek, selectedRoom, rooms.length]);

  // Verificar si una fecha pertenece a la semana actual (real)
  const isCurrentWeek = (date: Date | string) => {
    const checkDate = typeof date === 'string' ? new Date(date + 'T12:00:00') : date;
    const today = new Date();
    const todayWeekDates = getWeekDates(today);
    const dateStr = formatLocalDate(checkDate);
    return todayWeekDates.includes(dateStr);
  };

  // Verificar si la semana mostrada es la semana actual (real)
  const isViewingCurrentWeek = useMemo(() => {
    const today = new Date();
    const todayWeekDates = getWeekDates(today);
    return weekDates.some(d => todayWeekDates.includes(d));
  }, [weekDates]);

  // Contar clases sin publicar en la semana actual
  const unpublishedCount = useMemo(() => {
    return classes.filter(cls => !cls.isPublished).length;
  }, [classes]);

  // Contar clases con publicación programada
  const scheduledCount = useMemo(() => {
    return classes.filter(cls => !cls.isPublished && cls.scheduledPublicationDate).length;
  }, [classes]);

  // Obtener la próxima fecha de publicación programada
  const nextScheduledDate = useMemo(() => {
    const scheduled = classes
      .filter(cls => !cls.isPublished && cls.scheduledPublicationDate)
      .map(cls => {
        const date = cls.scheduledPublicationDate?.toDate
          ? cls.scheduledPublicationDate.toDate()
          : new Date(cls.scheduledPublicationDate);
        return date;
      })
      .sort((a, b) => a.getTime() - b.getTime());
    return scheduled[0] || null;
  }, [classes]);

  const goToPreviousWeek = () => {
    const newWeek = new Date(currentWeek);
    newWeek.setDate(currentWeek.getDate() - 7);
    setCurrentWeek(newWeek);
  };

  const goToNextWeek = () => {
    const newWeek = new Date(currentWeek);
    newWeek.setDate(currentWeek.getDate() + 7);
    setCurrentWeek(newWeek);
  };

  // Publicar todas las clases de la semana
  const publishWeek = async () => {
    const unpublishedClasses = classes.filter(cls => !cls.isPublished);
    if (unpublishedClasses.length === 0) {
      toast.error('No hay clases pendientes de publicar');
      return;
    }

    if (!confirm(`¿Publicar ${unpublishedClasses.length} clase(s)? Los usuarios podrán verlas y reservar.`)) {
      return;
    }

    try {
      const updatePromises = unpublishedClasses.map(cls =>
        updateDoc(doc(db, 'classes', cls.id), {
          isPublished: true,
          scheduledPublicationDate: null, // Limpiar programación si se publica manualmente
          updatedAt: serverTimestamp()
        })
      );
      await Promise.all(updatePromises);
      toast.success(`${unpublishedClasses.length} clase(s) publicada(s)`);
      await loadDataWithRetry();
    } catch (error) {
      console.error('Error publishing classes:', error);
      toast.error('Error al publicar las clases');
    }
  };

  const schedulePublication = async () => {
    const unpublishedClasses = classes.filter(cls => !cls.isPublished);
    console.log('📅 Programando publicación para', unpublishedClasses.length, 'clases');

    if (unpublishedClasses.length === 0) {
      toast.error('No hay clases en borrador para programar');
      return;
    }

    if (!scheduleDate || !scheduleTime) {
      toast.error('Selecciona fecha y hora');
      return;
    }

    const scheduledDate = new Date(`${scheduleDate}T${scheduleTime}:00`);
    console.log('📅 Fecha programada:', scheduledDate.toISOString());

    if (scheduledDate < new Date()) {
      toast.error('La fecha debe ser futura');
      return;
    }

    try {
      console.log('📅 Actualizando clases en Firestore...');
      for (const cls of unpublishedClasses) {
        console.log(`   - Actualizando clase: ${cls.name} (${cls.id})`);
        await updateDoc(doc(db, 'classes', cls.id), {
          scheduledPublicationDate: scheduledDate,
          updatedAt: serverTimestamp()
        });
      }
      console.log('✅ Todas las clases actualizadas');
      toast.success(`Publicación programada para ${unpublishedClasses.length} clase(s) - ${scheduleDate} ${scheduleTime}`);
      setShowScheduleModal(false);
      setScheduleDate('');
      setScheduleTime('17:00');
      await loadDataWithRetry();
    } catch (error: any) {
      console.error('❌ Error scheduling publication:', error);
      toast.error(`Error al programar: ${error.message || 'Error desconocido'}`);
    }
  };

  const checkFirebaseConnection = useCallback(async () => {
    try {
      const success = await firebaseConnectionManager.checkConnection();
      setIsOnline(success);
      return success;
    } catch {
      setIsOnline(false);
      return false;
    }
  }, []);

  const loadRooms = useCallback(async () => {
    if (!businessId) return;
    try {
      const businessDoc = await getDoc(doc(db, 'businesses', businessId));
      if (businessDoc.exists()) {
        const data = businessDoc.data();
        if (data.rooms && Array.isArray(data.rooms)) {
          setRooms(data.rooms);
        } else {
          setRooms([{ id: 'default', name: 'Sala Principal', capacity: 20, color: ROOM_COLORS[0] }]);
        }
      }
    } catch (error) {
      console.error('Error loading rooms:', error);
    }
  }, [businessId]);

  const saveRooms = async (updatedRooms: Room[]) => {
    if (!businessId) return;
    try {
      await updateDoc(doc(db, 'businesses', businessId), {
        rooms: updatedRooms,
        updatedAt: serverTimestamp()
      });
      setRooms(updatedRooms);
      toast.success('Salas actualizadas');
    } catch (error) {
      console.error('Error saving rooms:', error);
      toast.error('Error al guardar las salas');
    }
  };

  const addRoom = () => {
    if (!newRoomName.trim()) {
      toast.error('Introduce un nombre para la sala');
      return;
    }

    if (maxRooms !== -1 && rooms.length >= maxRooms) {
      toast.error(`Tu plan actual solo permite ${maxRooms} sala(s). Actualiza tu plan para añadir más.`);
      return;
    }

    const newRoom: Room = {
      id: `room_${Date.now()}`,
      name: newRoomName.trim(),
      capacity: newRoomCapacity,
      color: ROOM_COLORS[rooms.length % ROOM_COLORS.length]
    };
    saveRooms([...rooms, newRoom]);
    setNewRoomName('');
    setNewRoomCapacity(20);
  };

  const updateRoom = () => {
    if (!editingRoom) return;
    const updatedRooms = rooms.map(r => r.id === editingRoom.id ? editingRoom : r);
    saveRooms(updatedRooms);
    setEditingRoom(null);
  };

  const deleteRoom = (roomId: string) => {
    if (rooms.length <= 1) {
      toast.error('Debes tener al menos una sala');
      return;
    }
    if (!confirm('¿Eliminar esta sala?')) return;
    saveRooms(rooms.filter(r => r.id !== roomId));
  };

  const loadDataWithRetry = useCallback(async () => {
    if (!businessId) return;
    try {
      const startDate = weekDates[0];
      const endDate = weekDates[6];

      // Cargar clases programadas
      const classesQuery = query(
        collection(db, 'classes'),
        where('businessId', '==', businessId),
        where('isActive', '==', true)
      );
      const classesSnapshot = await getDocs(classesQuery);
      const classesData = classesSnapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() } as ScheduledClass))
        .filter(cls => cls.date >= startDate && cls.date <= endDate);
      setClasses(classesData);

      // Cargar instructores
      const instructorsQuery = query(
        collection(db, 'instructors'),
        where('businessId', '==', businessId),
        where('status', '==', 'active')
      );
      const instructorsSnapshot = await getDocs(instructorsQuery);
      setInstructors(instructorsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Instructor)));

      // Cargar tipos de clase (plantillas)
      const classTypesQuery = query(
        collection(db, 'classTypes'),
        where('businessId', '==', businessId),
        where('isActive', '==', true)
      );
      const classTypesSnapshot = await getDocs(classTypesQuery);
      setClassTypes(classTypesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as ClassType)));

      // Cargar usuarios
      const usersQuery = query(
        collection(db, 'users'),
        where('businessId', '==', businessId)
      );
      const usersSnapshot = await getDocs(usersQuery);
      setUsers(usersSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as User)));

      setIsOnline(true);
    } catch (error) {
      console.error('Error loading data:', error);
      setIsOnline(false);
    }
  }, [weekDates, businessId]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await loadRooms();
      await loadDataWithRetry();
      setLoading(false);
    };
    loadData();
  }, [loadDataWithRetry, loadRooms]);

  const reloadData = async () => {
    setLoading(true);
    await loadRooms();
    await loadDataWithRetry();
    setLoading(false);
  };

  const isSlotOccupied = (date: string, time: string, roomId: string, excludeClassId?: string) => {
    const slotMinutes = timeToMinutes(time);
    return classes.some(cls => {
      if (cls.date !== date || cls.roomId !== roomId) return false;
      if (excludeClassId && cls.id === excludeClassId) return false;
      const classStart = timeToMinutes(cls.time);
      const classEnd = classStart + cls.duration;
      return slotMinutes >= classStart && slotMinutes < classEnd;
    });
  };

  const getClassStartingAt = (date: string, time: string, roomId: string) => {
    return classes.find(cls => cls.date === date && cls.time === time && cls.roomId === roomId);
  };

  const handleCellClick = (date: string, time: string, roomId: string) => {
    const existingClass = getClassStartingAt(date, time, roomId);

    if (existingClass) {
      setSelectedClass(existingClass);
      setFormData({
        name: existingClass.name,
        instructor: existingClass.instructor,
        instructorId: existingClass.instructorId,
        time: existingClass.time,
        duration: existingClass.duration,
        maxCapacity: existingClass.maxCapacity,
        classType: existingClass.classType,
        description: existingClass.description || '',
        location: existingClass.location,
        roomId: existingClass.roomId || ''
      });
      setShowEditModal(true);
    } else if (!isSlotOccupied(date, time, roomId)) {
      setSelectedTimeSlot({ date, time });
      const room = rooms.find(r => r.id === roomId);

      // Reset form and mode
      setCreateMode('template');
      setSelectedTemplateId('');
      setFormData({
        name: '',
        instructor: '',
        instructorId: '',
        time,
        duration: 60,
        maxCapacity: room?.capacity || 15,
        classType: '',
        description: '',
        location: room?.name || 'Sala Principal',
        roomId: roomId
      });
      setShowCreateModal(true);
    }
  };

  // Cuando se selecciona una plantilla de clase
  const handleTemplateSelect = (templateId: string) => {
    setSelectedTemplateId(templateId);

    if (templateId === '') {
      // Reset to defaults
      const room = rooms.find(r => r.id === formData.roomId);
      setFormData(prev => ({
        ...prev,
        name: '',
        duration: 60,
        maxCapacity: room?.capacity || 15,
        classType: '',
        description: '',
        instructorId: '',
        instructor: ''
      }));
      return;
    }

    const template = classTypes.find(ct => ct.id === templateId);
    if (template) {
      // Buscar instructor asignado a esta clase
      let instructorId = '';
      let instructorName = '';

      if (template.instructorIds && template.instructorIds.length > 0) {
        const firstInstructorId = template.instructorIds[0];
        const instructor = instructors.find(i => i.id === firstInstructorId);
        if (instructor) {
          instructorId = instructor.id;
          instructorName = `${instructor.firstName} ${instructor.lastName}`;
        }
      }

      setFormData(prev => ({
        ...prev,
        name: template.name,
        duration: template.duration || 60,
        maxCapacity: template.maxCapacity || prev.maxCapacity,
        classType: template.id,
        description: template.description || '',
        instructorId,
        instructor: instructorName
      }));
    }
  };

  const validateNoOverlap = (date: string, time: string, duration: number, roomId: string, excludeClassId?: string): boolean => {
    const newStart = timeToMinutes(time);
    const newEnd = newStart + duration;

    return !classes.some(cls => {
      if (cls.date !== date || cls.roomId !== roomId) return false;
      if (excludeClassId && cls.id === excludeClassId) return false;
      const classStart = timeToMinutes(cls.time);
      const classEnd = classStart + cls.duration;
      return newStart < classEnd && newEnd > classStart;
    });
  };

  const createClass = async () => {
    if (!selectedTimeSlot || !businessId) return;

    const timeToUse = formData.time || selectedTimeSlot.time;

    if (!validateNoOverlap(selectedTimeSlot.date, timeToUse, formData.duration, formData.roomId)) {
      toast.error('La clase se solapa con otra existente');
      return;
    }

    if (!formData.name) {
      toast.error('Selecciona una clase o introduce un nombre');
      return;
    }

    try {
      const room = rooms.find(r => r.id === formData.roomId);
      const newClass = {
        name: formData.name,
        instructor: formData.instructor,
        instructorId: formData.instructorId,
        date: selectedTimeSlot.date,
        time: timeToUse,
        duration: formData.duration,
        maxCapacity: formData.maxCapacity,
        classType: formData.classType,
        description: formData.description,
        location: room?.name || formData.location,
        roomId: formData.roomId,
        businessId,
        enrolled: {},
        isActive: true,
        isPublished: isCurrentWeek(selectedTimeSlot?.date || new Date()), // Auto-publicar solo la semana actual
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      await addDoc(collection(db, 'classes'), newClass);
      toast.success('Clase programada');
      setShowCreateModal(false);
      setSelectedTimeSlot(null);
      resetForm();
      await loadDataWithRetry();
    } catch (error) {
      console.error('Error creating class:', error);
      toast.error('Error al crear la clase');
    }
  };

  const updateClass = async () => {
    if (!selectedClass) return;

    if (!validateNoOverlap(selectedClass.date, formData.time, formData.duration, formData.roomId, selectedClass.id)) {
      toast.error('La clase se solapa con otra existente');
      return;
    }

    try {
      const room = rooms.find(r => r.id === formData.roomId);
      await updateDoc(doc(db, 'classes', selectedClass.id), {
        ...formData,
        location: room?.name || formData.location,
        updatedAt: serverTimestamp()
      });
      toast.success('Clase actualizada');
      setShowEditModal(false);
      setSelectedClass(null);
      resetForm();
      await loadDataWithRetry();
    } catch (error) {
      console.error('Error updating class:', error);
      toast.error('Error al actualizar');
    }
  };

  const deleteClass = async (classId: string) => {
    if (!confirm('¿Eliminar esta clase?')) return;
    try {
      await updateDoc(doc(db, 'classes', classId), {
        isActive: false,
        updatedAt: serverTimestamp()
      });
      toast.success('Clase eliminada');
      setShowEditModal(false);
      setSelectedClass(null);
      await loadDataWithRetry();
    } catch (error) {
      console.error('Error deleting class:', error);
      toast.error('Error al eliminar');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      instructor: '',
      instructorId: '',
      time: '',
      duration: 60,
      maxCapacity: 15,
      classType: '',
      description: '',
      location: 'Sala Principal',
      roomId: rooms[0]?.id || ''
    });
    setSelectedTemplateId('');
    setCreateMode('template');
  };

  // === CLONAR SEMANA ===
  const cloneWeekToTargets = async () => {
    if (!businessId || classes.length === 0) return;
    setIsCloning(true);

    let totalCreated = 0;
    let totalSkipped = 0;

    try {
      for (let weekOffset = 1; weekOffset <= cloneWeeksCount; weekOffset++) {
        // Calcular fechas de la semana destino
        const targetWeekDates = weekDates.map(dateStr => {
          const d = new Date(dateStr + 'T12:00:00');
          d.setDate(d.getDate() + 7 * weekOffset);
          return formatLocalDate(d);
        });

        // Cargar clases existentes en la semana destino
        const targetStartDate = targetWeekDates[0];
        const targetEndDate = targetWeekDates[6];
        const existingQuery = query(
          collection(db, 'classes'),
          where('businessId', '==', businessId),
          where('isActive', '==', true)
        );
        const existingSnapshot = await getDocs(existingQuery);
        const existingClasses = existingSnapshot.docs
          .map(d => ({ id: d.id, ...d.data() } as ScheduledClass))
          .filter(cls => cls.date >= targetStartDate && cls.date <= targetEndDate);

        // Comprobar solapamiento en la semana destino
        const hasOverlap = (date: string, time: string, duration: number, roomId: string): boolean => {
          const newStart = timeToMinutes(time);
          const newEnd = newStart + duration;
          return existingClasses.some(cls => {
            if (cls.date !== date || cls.roomId !== roomId) return false;
            const clsStart = timeToMinutes(cls.time);
            const clsEnd = clsStart + cls.duration;
            return newStart < clsEnd && newEnd > clsStart;
          });
        };

        // Clonar cada clase
        for (const cls of classes) {
          // Encontrar el índice del día de la semana origen
          const dayIndex = weekDates.indexOf(cls.date);
          if (dayIndex === -1) continue;

          const targetDate = targetWeekDates[dayIndex];

          if (hasOverlap(targetDate, cls.time, cls.duration, cls.roomId || '')) {
            totalSkipped++;
            continue;
          }

          const newClass = {
            name: cls.name,
            instructor: cls.instructor,
            instructorId: cls.instructorId,
            date: targetDate,
            time: cls.time,
            duration: cls.duration,
            maxCapacity: cls.maxCapacity,
            classType: cls.classType,
            description: cls.description || '',
            location: cls.location,
            roomId: cls.roomId || '',
            businessId,
            enrolled: {},
            isActive: true,
            isPublished: false,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp()
          };

          await addDoc(collection(db, 'classes'), newClass);
          totalCreated++;

          // Añadir a la lista de existentes para evitar duplicados intra-batch
          existingClasses.push({
            ...newClass,
            id: 'temp_' + Date.now() + '_' + totalCreated,
            enrolled: {},
            isPublished: false,
            createdAt: null,
            updatedAt: null
          } as ScheduledClass);
        }
      }

      if (totalCreated > 0) {
        toast.success(
          `✅ ${totalCreated} clase${totalCreated > 1 ? 's' : ''} clonada${totalCreated > 1 ? 's' : ''}` +
          (totalSkipped > 0 ? ` (${totalSkipped} omitida${totalSkipped > 1 ? 's' : ''} por conflicto)` : '') +
          ` como borrador`
        );
      } else {
        toast('No se clonaron clases — todas las semanas destino ya tienen las mismas clases', { icon: 'ℹ️' });
      }

      setShowCloneModal(false);
      setCloneWeeksCount(1);
      await loadDataWithRetry();
    } catch (error) {
      console.error('Error cloning week:', error);
      toast.error('Error al clonar la semana');
    } finally {
      setIsCloning(false);
    }
  };

  const getInstructorName = (instructorId: string) => {
    const instructor = instructors.find(inst => inst.id === instructorId);
    return instructor ? `${instructor.firstName} ${instructor.lastName}` : '';
  };

  const getEnrolledCount = (enrolled: { [userId: string]: boolean }) => {
    return Object.values(enrolled).filter(Boolean).length;
  };

  const getEnrolledUsers = (enrolled: { [userId: string]: boolean }) => {
    return Object.keys(enrolled).filter(userId => enrolled[userId]);
  };

  const getUserName = (userId: string) => {
    const user = users.find(u => u.id === userId);
    return user ? `${user.firstName} ${user.lastName}` : `Usuario ${userId.slice(0, 6)}...`;
  };

  const getUserPhoto = (userId: string) => {
    const user = users.find(u => u.id === userId);
    return user?.profilePhotoUrl || user?.photoUrl;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-950">
        <div className="text-center">
          <Loading size="lg" className="mx-auto mb-4" />
          <p className="text-gray-400">Cargando calendario...</p>
        </div>
      </div>
    );
  }

  const displayRooms = selectedRoom === 'all' ? rooms : rooms.filter(r => r.id === selectedRoom);

  // Componente para renderizar las clases de una sala en un día específico
  const RoomDayColumn = ({ room, date, roomClasses }: { room: Room; date: string; roomClasses: ScheduledClass[] }) => {
    const dayClasses = roomClasses.filter(cls => cls.date === date);

    return (
      <div className="flex-1 relative" style={{ minWidth: '80px' }}>
        {TIME_SLOTS.map((time) => {
          const isOccupied = isSlotOccupied(date, time, room.id);
          return (
            <div
              key={time}
              className={`border-b border-gray-800/50 transition-colors bg-gray-900 ${!isOccupied ? 'hover:bg-gray-800/50 cursor-pointer' : ''
                } ${time.endsWith(':00') ? 'border-gray-700' : ''}`}
              style={{ height: SLOT_HEIGHT }}
              onClick={() => !isOccupied && handleCellClick(date, time, room.id)}
            >
              {!isOccupied && (
                <div className="w-full h-full flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity">
                  <Plus className="w-3 h-3 text-gray-600" />
                </div>
              )}
            </div>
          );
        })}

        {dayClasses.map(cls => {
          const startIdx = getSlotIndex(cls.time);
          const slotsCount = getSlotsCount(cls.duration);
          const top = startIdx * SLOT_HEIGHT;
          const height = slotsCount * SLOT_HEIGHT - 2;
          const classTypeData = classTypes.find(ct => ct.id === cls.classType);
          const classImage = classTypeData?.image;

          return (
            <div
              key={cls.id}
              className={`absolute left-0.5 right-0.5 rounded-lg overflow-hidden cursor-pointer hover:ring-2 hover:ring-white/30 transition-all ${!cls.isPublished ? 'border-2 border-dashed border-white/50 opacity-70' : ''
                }`}
              style={{
                top: top + 1,
                height,
                backgroundColor: room.color,
              }}
              onClick={(e) => {
                e.stopPropagation();
                setSelectedClass(cls);
                setFormData({
                  name: cls.name,
                  instructor: cls.instructor,
                  instructorId: cls.instructorId,
                  time: cls.time,
                  duration: cls.duration,
                  maxCapacity: cls.maxCapacity,
                  classType: cls.classType,
                  description: cls.description || '',
                  location: cls.location,
                  roomId: cls.roomId || ''
                });
                setShowEditModal(true);
              }}
            >
              {classImage && (
                <div
                  className="absolute inset-0 bg-cover bg-center"
                  style={{ backgroundImage: `url(${classImage})` }}
                >
                  <div className="absolute inset-0 bg-black/50" />
                </div>
              )}
              <div className="relative p-1 h-full flex flex-col z-10">
                <div className="font-medium text-white text-[10px] truncate flex items-center gap-0.5">
                  {!cls.isPublished && <span className="text-[7px] bg-white/20 px-0.5 rounded">B</span>}
                  {cls.name}
                </div>
                {height > 50 && (
                  <div className="text-white/80 text-[9px] truncate">
                    {getInstructorName(cls.instructorId)}
                  </div>
                )}
                {height > 70 && (
                  <div className="text-white/70 text-[9px] flex items-center gap-0.5 mt-auto">
                    <Users className="w-2.5 h-2.5" />
                    {getEnrolledCount(cls.enrolled)}/{cls.maxCapacity}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  // Calendario unificado con todas las salas lado a lado
  const UnifiedCalendar = () => {
    return (
      <Card className="overflow-hidden bg-gray-900 border-gray-800">
        <div ref={unifiedCalendarScrollRef} className="overflow-auto max-h-[calc(100vh-200px)]">
          {/* Usar min-w-max para permitir que el contenido crezca según lo necesario */}
          <div className="min-w-max p-1">
            {/* Header con nombres de salas y días */}
            <div className="flex border-b border-gray-800 sticky top-0 z-20 bg-gray-900">
              {/* Columna de horas */}
              <div className="w-16 flex-shrink-0 bg-gray-900 p-2 text-xs text-gray-400 font-medium sticky left-0 z-30">
                Hora
              </div>

              {/* Headers de salas */}
              {displayRooms.map((room, index) => (
                <div
                  key={room.id}
                  className="flex-shrink-0"
                  style={{
                    width: `${weekDates.length * 100}px`,
                    marginLeft: index > 0 ? '12px' : '0'
                  }}
                >
                  {/* Nombre de sala */}
                  <div
                    className="px-3 py-2 flex items-center gap-2 rounded-t-lg"
                    style={{
                      backgroundColor: `${room.color}30`,
                      borderBottom: `3px solid ${room.color}`,
                      borderLeft: `3px solid ${room.color}`,
                      borderRight: `3px solid ${room.color}`,
                      borderTop: `3px solid ${room.color}`
                    }}
                  >
                    <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: room.color }} />
                    <span className="font-semibold text-white text-sm truncate">{room.name}</span>
                    <span className="text-xs text-gray-400 hidden sm:inline">({room.capacity})</span>
                  </div>

                  {/* Días de la semana */}
                  <div
                    className="flex bg-gray-900" // Asegurar fondo opaco
                    style={{
                      borderLeft: `3px solid ${room.color}`,
                      borderRight: `3px solid ${room.color}`
                    }}
                  >
                    {weekDates.map((date, dateIndex) => (
                      <div
                        key={date}
                        className={`flex-1 p-1.5 text-center ${dateIndex > 0 ? 'border-l border-gray-700' : ''}`}
                        title={`Fecha interna: ${date}`}
                      >
                        <div className="font-semibold text-gray-300 text-xs">
                          {new Date(date + 'T12:00:00').toLocaleDateString('es-ES', { weekday: 'short' })}
                        </div>
                        <div className="text-[10px] text-gray-500">
                          {new Date(date + 'T12:00:00').toLocaleDateString('es-ES', { day: 'numeric', month: 'short' })}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {/* Grid de horas y clases */}
            <div className="flex">
              {/* Columna de horas */}
              <div className="w-16 flex-shrink-0 bg-gray-900 sticky left-0 z-10 border-r border-gray-800">
                {TIME_SLOTS.map((time) => (
                  <div
                    key={time}
                    className="border-b border-gray-800 text-xs text-gray-500 px-2 flex items-center bg-gray-900"
                    style={{ height: SLOT_HEIGHT }}
                  >
                    {time.endsWith(':00') ? time : ''}
                  </div>
                ))}
              </div>

              {/* Columnas de salas */}
              {displayRooms.map((room, index) => {
                const roomClasses = classes.filter(cls => cls.roomId === room.id);
                return (
                  <div
                    key={room.id}
                    className="flex-shrink-0"
                    style={{
                      width: `${weekDates.length * 100}px`,
                      marginLeft: index > 0 ? '12px' : '0',
                      borderLeft: `3px solid ${room.color}`,
                      borderRight: `3px solid ${room.color}`,
                      borderBottom: `3px solid ${room.color}`,
                      borderRadius: '0 0 8px 8px',
                      overflow: 'hidden',
                      backgroundColor: '#111827'
                    }}
                  >
                    <div className="grid grid-cols-7 w-full">
                      {weekDates.map((date, dateIndex) => (
                        <div
                          key={`${room.id}-${date}`}
                          className={dateIndex > 0 ? 'border-l border-gray-700' : ''}
                          style={{ minWidth: '0' }}
                        >
                          <RoomDayColumn room={room} date={date} roomClasses={roomClasses} />
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </Card>
    );
  };

  // Calendario de una sola sala (mantener diseño original para mejor visualización)
  const SingleRoomCalendar = ({ room }: { room: Room }) => {
    const roomClasses = classes.filter(cls => cls.roomId === room.id);

    return (
      <Card className="overflow-hidden bg-gray-900 border-gray-800">
        <div
          className="px-4 py-3 flex items-center gap-3"
          style={{ backgroundColor: `${room.color}20`, borderBottom: `2px solid ${room.color}` }}
        >
          <div className="w-4 h-4 rounded-full" style={{ backgroundColor: room.color }} />
          <h3 className="font-semibold text-white">{room.name}</h3>
          <span className="text-sm text-gray-400">Capacidad: {room.capacity}</span>
        </div>

        <div ref={singleCalendarScrollRef} className="overflow-auto w-full max-h-[calc(100vh-200px)]">
          <div className="w-full min-w-[900px]">
            <div className="flex border-b border-gray-800 sticky top-0 z-20 bg-gray-900">
              <div className="w-16 flex-shrink-0 bg-gray-900 p-2 text-xs text-gray-400 font-medium sticky left-0 z-30">
                Hora
              </div>
              {weekDates.map((date) => (
                <div key={date} className="flex-1 bg-gray-900 p-2 text-center border-l border-gray-700">
                  <div className="font-semibold text-gray-300 text-sm">
                    {new Date(date + 'T12:00:00').toLocaleDateString('es-ES', { weekday: 'short' })}
                  </div>
                  <div className="text-xs text-gray-500">
                    {new Date(date + 'T12:00:00').toLocaleDateString('es-ES', { day: 'numeric', month: 'short' })}
                  </div>
                </div>
              ))}
            </div>

            <div className="flex">
              <div className="w-16 flex-shrink-0 bg-gray-900 sticky left-0 z-10 border-r border-gray-800">
                {TIME_SLOTS.map((time) => (
                  <div
                    key={time}
                    className="border-b border-gray-800 text-xs text-gray-500 px-2 flex items-center"
                    style={{ height: SLOT_HEIGHT }}
                  >
                    {time.endsWith(':00') ? time : ''}
                  </div>
                ))}
              </div>

              {/* Usar Grid para asegurar 7 columnas exactas sin cortes */}
              <div className="grid grid-cols-7 flex-1">
                {weekDates.map((date, index) => (
                  <div
                    key={`${room.id}-${date}`}
                    className={index > 0 ? 'border-l border-gray-700' : ''}
                  >
                    <RoomDayColumn room={room} date={date} roomClasses={roomClasses} />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </Card>

    );
  };

  return (
    <>
      <Helmet>
        <title>Gestión de Calendario | {currentBusiness?.name || 'Admin'}</title>
      </Helmet>

      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0 flex-1">
            <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-amber-400">Gestión de Calendario</h1>
            <div className="flex items-center gap-2 mt-1">
              {isOnline ? (
                <div className="flex items-center gap-1 text-green-400">
                  <Wifi className="h-3.5 w-3.5" />
                  <span className="text-xs">Conectado</span>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <WifiOff className="h-3.5 w-3.5 text-red-400" />
                  <span className="text-xs text-red-400">Desconectado</span>
                  <Button size="sm" variant="outline" onClick={checkFirebaseConnection}>
                    Reconectar
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Action Buttons - Mobile-friendly grid */}
        <div className="grid grid-cols-4 gap-2 sm:gap-3">
          <button
            onClick={() => setShowRoomConfig(true)}
            className="flex flex-col items-center gap-1.5 p-3 sm:p-4 rounded-xl bg-gradient-to-b from-gray-800 to-gray-900 border border-gray-700/50 hover:border-blue-500/50 hover:from-gray-750 hover:to-gray-850 transition-all active:scale-95 group"
          >
            <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-blue-500/15 flex items-center justify-center group-hover:bg-blue-500/25 transition-colors">
              <DoorOpen className="w-5 h-5 sm:w-5.5 sm:h-5.5 text-blue-400" />
            </div>
            <span className="text-[11px] sm:text-xs font-medium text-gray-300 group-hover:text-white transition-colors">Salas</span>
            <span className="text-[9px] sm:text-[10px] text-gray-500 -mt-1">{rooms.length} sala{rooms.length !== 1 ? 's' : ''}</span>
          </button>

          <button
            onClick={() => setShowCloneModal(true)}
            disabled={classes.length === 0}
            className="flex flex-col items-center gap-1.5 p-3 sm:p-4 rounded-xl bg-gradient-to-b from-gray-800 to-gray-900 border border-gray-700/50 hover:border-amber-500/50 hover:from-gray-750 hover:to-gray-850 transition-all active:scale-95 group disabled:opacity-40 disabled:pointer-events-none"
          >
            <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-amber-500/15 flex items-center justify-center group-hover:bg-amber-500/25 transition-colors">
              <Copy className="w-5 h-5 sm:w-5.5 sm:h-5.5 text-amber-400" />
            </div>
            <span className="text-[11px] sm:text-xs font-medium text-gray-300 group-hover:text-white transition-colors">Clonar</span>
            <span className="text-[9px] sm:text-[10px] text-gray-500 -mt-1">{classes.length} clase{classes.length !== 1 ? 's' : ''}</span>
          </button>

          <button
            onClick={() => setCurrentWeek(new Date())}
            className="flex flex-col items-center gap-1.5 p-3 sm:p-4 rounded-xl bg-gradient-to-b from-gray-800 to-gray-900 border border-gray-700/50 hover:border-green-500/50 hover:from-gray-750 hover:to-gray-850 transition-all active:scale-95 group"
          >
            <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-green-500/15 flex items-center justify-center group-hover:bg-green-500/25 transition-colors">
              <Calendar className="w-5 h-5 sm:w-5.5 sm:h-5.5 text-green-400" />
            </div>
            <span className="text-[11px] sm:text-xs font-medium text-gray-300 group-hover:text-white transition-colors">Hoy</span>
            <span className="text-[9px] sm:text-[10px] text-gray-500 -mt-1">Ir a hoy</span>
          </button>

          <button
            onClick={reloadData}
            disabled={loading}
            className="flex flex-col items-center gap-1.5 p-3 sm:p-4 rounded-xl bg-gradient-to-b from-gray-800 to-gray-900 border border-gray-700/50 hover:border-purple-500/50 hover:from-gray-750 hover:to-gray-850 transition-all active:scale-95 group disabled:opacity-40"
          >
            <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-purple-500/15 flex items-center justify-center group-hover:bg-purple-500/25 transition-colors">
              <RefreshCw className={`w-5 h-5 sm:w-5.5 sm:h-5.5 text-purple-400 ${loading ? 'animate-spin' : ''}`} />
            </div>
            <span className="text-[11px] sm:text-xs font-medium text-gray-300 group-hover:text-white transition-colors">Refrescar</span>
            <span className="text-[9px] sm:text-[10px] text-gray-500 -mt-1">Recargar</span>
          </button>
        </div>

        <Card className="p-4 bg-gray-900 border-gray-800">
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-gray-400 text-sm font-medium">Ver:</span>
            <button
              onClick={() => setSelectedRoom('all')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${selectedRoom === 'all' ? 'bg-amber-500 text-white' : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                }`}
            >
              Todas
            </button>
            {rooms.map((room) => (
              <button
                key={room.id}
                onClick={() => setSelectedRoom(room.id)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${selectedRoom === room.id ? 'bg-amber-500 text-white' : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                  }`}
              >
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: room.color }} />
                {room.name}
              </button>
            ))}
          </div>
        </Card>

        <Card className="p-4 bg-gray-900 border-gray-800">
          <div className="flex flex-col gap-4">
            <div className="flex items-center justify-between gap-2">
              <Button variant="outline" size="sm" onClick={goToPreviousWeek} className="px-2 sm:px-3">
                <ChevronLeft className="w-4 h-4 sm:mr-1" />
                <span className="hidden sm:inline text-sm">Anterior</span>
              </Button>
              <div className="text-center flex-1 min-w-0">
                <h2 className="text-base sm:text-lg font-semibold text-white truncate">
                  {currentWeek.toLocaleDateString('es-ES', { month: 'long', year: 'numeric' })}
                </h2>
                <p className="text-xs sm:text-sm text-gray-400">
                  {weekDates[0]} - {weekDates[6]}
                </p>
                {isViewingCurrentWeek && (
                  <span className="inline-block mt-1 px-2 py-0.5 bg-green-500/20 text-green-400 text-xs rounded-full">
                    Semana actual
                  </span>
                )}
              </div>
              <Button variant="outline" size="sm" onClick={goToNextWeek} className="px-2 sm:px-3">
                <span className="hidden sm:inline text-sm">Siguiente</span>
                <ChevronRight className="w-4 h-4 sm:ml-1" />
              </Button>
            </div>

            {unpublishedCount > 0 && (
              <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg space-y-2">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  {/* Info */}
                  <div className="flex flex-col gap-1">
                    <div className="flex items-center gap-2">
                      <div className="w-2.5 h-2.5 bg-amber-500 rounded-full animate-pulse flex-shrink-0" />
                      <span className="text-amber-400 text-sm font-medium">
                        {unpublishedCount} clase{unpublishedCount > 1 ? 's' : ''} en borrador
                      </span>
                    </div>
                    {scheduledCount > 0 && nextScheduledDate && (
                      <div className="flex items-center gap-2 ml-4">
                        <Clock className="w-3 h-3 text-green-400" />
                        <span className="text-green-400 text-xs">
                          {scheduledCount} programada{scheduledCount > 1 ? 's' : ''} para{' '}
                          {nextScheduledDate.toLocaleDateString('es-ES', {
                            day: 'numeric',
                            month: 'short',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </span>
                      </div>
                    )}
                  </div>
                  {/* Schedule button */}
                  <Button
                    size="sm"
                    className="bg-amber-500 hover:bg-amber-600 text-black font-semibold text-xs sm:text-sm"
                    onClick={() => setShowScheduleModal(true)}
                  >
                    <Clock className="w-3.5 h-3.5 mr-1.5" />
                    {scheduledCount > 0 ? 'Reprogramar' : 'Programar publicación'}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </Card>

        {/* Calendario unificado o individual según cantidad de salas */}
        {displayRooms.length > 1 ? (
          <UnifiedCalendar />
        ) : displayRooms.length === 1 ? (
          <SingleRoomCalendar room={displayRooms[0]} />
        ) : (
          <Card className="p-8 text-center bg-gray-900 border-gray-800">
            <DoorOpen className="w-12 h-12 mx-auto text-gray-600 mb-3" />
            <p className="text-gray-400">No hay salas configuradas</p>
            <Button
              className="mt-4 bg-amber-500 hover:bg-amber-600 text-black"
              onClick={() => setShowRoomConfig(true)}
            >
              Configurar Salas
            </Button>
          </Card>
        )}
      </div>

      {/* Schedule Publication Modal */}
      {showScheduleModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <Card className="p-6 w-full max-w-sm bg-gray-900 border-gray-800">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-amber-400 flex items-center gap-2">
                <Clock className="w-5 h-5" />
                Programar Publicación
              </h3>
              <Button variant="ghost" size="sm" onClick={() => setShowScheduleModal(false)}>
                <X className="w-4 h-4" />
              </Button>
            </div>

            <div className="p-3 bg-amber-500/10 border border-amber-500/20 rounded-lg mb-4">
              <p className="text-xs text-amber-300">
                <strong>{unpublishedCount} clase{unpublishedCount > 1 ? 's' : ''}</strong> se publicarán automáticamente en la fecha y hora seleccionadas.
              </p>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Fecha de publicación</label>
                <Input
                  type="date"
                  value={scheduleDate}
                  onChange={(e) => setScheduleDate(e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                  className="bg-gray-800"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Hora</label>
                <Input
                  type="time"
                  value={scheduleTime}
                  onChange={(e) => setScheduleTime(e.target.value)}
                  className="bg-gray-800"
                />
              </div>

              {scheduleDate && scheduleTime && (
                <div className="p-3 bg-green-500/10 border border-green-500/20 rounded-lg">
                  <p className="text-xs text-green-300 flex items-center gap-2">
                    <CheckCircle className="w-4 h-4" />
                    Las clases serán visibles el {new Date(`${scheduleDate}T${scheduleTime}`).toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long' })} a las {scheduleTime}
                  </p>
                </div>
              )}

              <div className="flex gap-2 pt-2">
                <Button
                  variant="ghost"
                  className="flex-1"
                  onClick={() => setShowScheduleModal(false)}
                >
                  Cancelar
                </Button>
                <Button
                  className="flex-1 bg-amber-500 text-black hover:bg-amber-600 font-semibold"
                  onClick={schedulePublication}
                  disabled={!scheduleDate || !scheduleTime}
                >
                  Confirmar
                </Button>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Room Config Modal */}
      {showRoomConfig && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <Card className="p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto bg-gray-900 border-gray-800">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-semibold text-amber-400 flex items-center gap-2">
                <DoorOpen className="w-5 h-5" />
                Configurar Salas
              </h3>
              <Button variant="ghost" size="sm" onClick={() => setShowRoomConfig(false)}>
                <X className="w-4 h-4" />
              </Button>
            </div>

            <div className="mb-6 p-4 bg-gray-800 rounded-lg">
              <h4 className="text-sm font-medium text-gray-300 mb-3">Añadir Sala</h4>
              <div className="flex gap-3">
                <Input
                  value={newRoomName}
                  onChange={(e) => setNewRoomName(e.target.value)}
                  placeholder="Nombre"
                  className="flex-1"
                />
                <Input
                  type="number"
                  value={newRoomCapacity}
                  onChange={(e) => setNewRoomCapacity(parseInt(e.target.value) || 20)}
                  className="w-20"
                  min="1"
                />
                <Button onClick={addRoom}><Plus className="w-4 h-4" /></Button>
              </div>
            </div>

            <div className="space-y-3">
              {rooms.map((room) => (
                <div key={room.id} className="flex items-center gap-3 p-3 bg-gray-800 rounded-lg">
                  <div
                    className="w-4 h-4 rounded-full cursor-pointer"
                    style={{ backgroundColor: room.color }}
                    onClick={() => {
                      const newColor = ROOM_COLORS[(ROOM_COLORS.indexOf(room.color) + 1) % ROOM_COLORS.length];
                      saveRooms(rooms.map(r => r.id === room.id ? { ...r, color: newColor } : r));
                    }}
                  />
                  {editingRoom?.id === room.id ? (
                    <>
                      <Input
                        value={editingRoom.name}
                        onChange={(e) => setEditingRoom({ ...editingRoom, name: e.target.value })}
                        className="flex-1"
                      />
                      <Input
                        type="number"
                        value={editingRoom.capacity}
                        onChange={(e) => setEditingRoom({ ...editingRoom, capacity: parseInt(e.target.value) || 20 })}
                        className="w-16"
                      />
                      <Button size="sm" onClick={updateRoom}><Save className="w-4 h-4" /></Button>
                      <Button size="sm" variant="ghost" onClick={() => setEditingRoom(null)}><X className="w-4 h-4" /></Button>
                    </>
                  ) : (
                    <>
                      <span className="flex-1 text-white">{room.name}</span>
                      <span className="text-sm text-gray-400">{room.capacity}</span>
                      <Button size="sm" variant="ghost" onClick={() => setEditingRoom(room)}><Edit className="w-4 h-4" /></Button>
                      <Button size="sm" variant="ghost" className="text-red-400" onClick={() => deleteRoom(room.id)}><Trash2 className="w-4 h-4" /></Button>
                    </>
                  )}
                </div>
              ))}
            </div>

            <Button variant="outline" onClick={() => setShowRoomConfig(false)} className="w-full mt-6">
              Cerrar
            </Button>
          </Card>
        </div>
      )}

      {/* Create Class Modal */}
      {showCreateModal && selectedTimeSlot && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <Card className="p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-gray-900 border-gray-800">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-amber-400">
                Programar Clase - {new Date(selectedTimeSlot.date + 'T12:00:00').toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'short' })} {selectedTimeSlot.time}
              </h3>
              <Button variant="ghost" size="sm" onClick={() => { setShowCreateModal(false); resetForm(); }}>
                <X className="w-4 h-4" />
              </Button>
            </div>

            {/* Mode selector */}
            <div className="flex gap-2 mb-6">
              <button
                onClick={() => setCreateMode('template')}
                className={`flex-1 py-3 px-4 rounded-lg flex items-center justify-center gap-2 transition-all ${createMode === 'template'
                  ? 'bg-amber-500 text-white'
                  : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                  }`}
              >
                <Zap className="w-5 h-5" />
                Usar clase existente
              </button>
              <button
                onClick={() => { setCreateMode('manual'); setSelectedTemplateId(''); }}
                className={`flex-1 py-3 px-4 rounded-lg flex items-center justify-center gap-2 transition-all ${createMode === 'manual'
                  ? 'bg-amber-500 text-white'
                  : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                  }`}
              >
                <Edit className="w-5 h-5" />
                Crear manualmente
              </button>
            </div>

            <div className="space-y-4">
              {/* Template selector (only in template mode) */}
              {createMode === 'template' && (
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Selecciona una clase
                  </label>
                  <select
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white text-lg"
                    value={selectedTemplateId}
                    onChange={(e) => handleTemplateSelect(e.target.value)}
                  >
                    <option value="">-- Selecciona una clase --</option>
                    {classTypes.map(ct => (
                      <option key={ct.id} value={ct.id}>
                        {ct.name} ({ct.duration || 60} min)
                      </option>
                    ))}
                  </select>
                  {classTypes.length === 0 && (
                    <p className="text-sm text-amber-400 mt-2">
                      No hay clases creadas. Ve a "Clases" para crear algunas.
                    </p>
                  )}
                </div>
              )}

              {/* Show form fields if template selected or manual mode */}
              {(createMode === 'manual' || selectedTemplateId) && (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Nombre</label>
                      <Input
                        value={formData.name}
                        onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                        placeholder="Nombre de la clase"
                        disabled={createMode === 'template' && !!selectedTemplateId}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Sala</label>
                      <select
                        className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white"
                        value={formData.roomId}
                        onChange={(e) => setFormData(prev => ({ ...prev, roomId: e.target.value }))}
                      >
                        {rooms.map(room => (
                          <option key={room.id} value={room.id}>{room.name}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Hora</label>
                      <select
                        className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white"
                        value={formData.time}
                        onChange={(e) => setFormData(prev => ({ ...prev, time: e.target.value }))}
                      >
                        {TIME_SLOTS.map(time => (
                          <option key={time} value={time}>{time}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Duración</label>
                      <select
                        className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white"
                        value={formData.duration}
                        onChange={(e) => setFormData(prev => ({ ...prev, duration: parseInt(e.target.value) }))}
                      >
                        {DURATION_OPTIONS.map(opt => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Capacidad Máxima</label>
                      <Input
                        type="number"
                        value={formData.maxCapacity}
                        onChange={(e) => setFormData(prev => ({ ...prev, maxCapacity: parseInt(e.target.value) || 0 }))}
                        min="1"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Capacidad de la sala: {rooms.find(r => r.id === formData.roomId)?.capacity || 0}
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Instructor</label>
                      <select
                        className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white"
                        value={formData.instructorId}
                        onChange={(e) => {
                          const instructor = instructors.find(i => i.id === e.target.value);
                          setFormData(prev => ({
                            ...prev,
                            instructorId: e.target.value,
                            instructor: instructor ? `${instructor.firstName} ${instructor.lastName}` : ''
                          }));
                        }}
                      >
                        <option value="">-- Seleccionar --</option>
                        {(() => {
                          // Filtrar instructores según la clase seleccionada
                          // Método 1: Usar instructorIds del tipo de clase
                          // Método 2: Usar classTypeIds del instructor
                          const selectedTemplate = classTypes.find(ct => ct.id === selectedTemplateId);

                          // Primero intentar con instructorIds del tipo de clase
                          const allowedFromClassType = selectedTemplate?.instructorIds || [];

                          // También buscar instructores que tengan este tipo de clase en sus classTypeIds
                          const allowedFromInstructor = instructors
                            .filter(inst => inst.classTypeIds?.includes(selectedTemplateId))
                            .map(inst => inst.id);

                          // Combinar ambos métodos (union de los dos conjuntos)
                          const allAllowedIds = [...new Set([...allowedFromClassType, ...allowedFromInstructor])];

                          // Si hay instructores asignados por cualquier método, filtrar
                          // Si no hay ninguno, NO mostrar todos (mostrar vacío)
                          const filteredInstructors = allAllowedIds.length > 0
                            ? instructors.filter(inst => allAllowedIds.includes(inst.id))
                            : [];

                          return filteredInstructors.map(inst => (
                            <option key={inst.id} value={inst.id}>
                              {inst.firstName} {inst.lastName}
                            </option>
                          ));
                        })()}
                      </select>
                      {(() => {
                        const selectedTemplate = classTypes.find(ct => ct.id === selectedTemplateId);
                        const allowedFromClassType = selectedTemplate?.instructorIds || [];
                        const allowedFromInstructor = instructors
                          .filter(inst => inst.classTypeIds?.includes(selectedTemplateId))
                          .map(inst => inst.id);
                        const allAllowedIds = [...new Set([...allowedFromClassType, ...allowedFromInstructor])];

                        if (allAllowedIds.length === 0) {
                          return <p className="text-xs text-amber-400 mt-1">⚠️ No hay instructores asignados a esta clase</p>;
                        }
                        return null;
                      })()}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Descripción (Opcional)</label>
                    <Input
                      value={formData.description}
                      onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                      placeholder="Detalles adicionales..."
                    />
                  </div>
                </>
              )}

              <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-800">
                <Button variant="ghost" onClick={() => { setShowCreateModal(false); resetForm(); }}>
                  Cancelar
                </Button>
                <Button
                  onClick={createClass}
                  disabled={!selectedTemplateId && createMode === 'template'}
                  className="bg-amber-500 hover:bg-amber-600 text-black"
                >
                  Programar Clase
                </Button>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Edit Class Modal */}
      {showEditModal && selectedClass && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <Card className="p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto bg-gray-900 border-gray-800">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-lg font-semibold text-amber-400">Editar Clase</h3>
              <Button variant="ghost" size="sm" onClick={() => setShowEditModal(false)}>
                <X className="w-4 h-4" />
              </Button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Nombre</label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Sala</label>
                  <select
                    className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white"
                    value={formData.roomId}
                    onChange={(e) => setFormData(prev => ({ ...prev, roomId: e.target.value }))}
                  >
                    {rooms.map(room => (
                      <option key={room.id} value={room.id}>{room.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Instructor</label>
                  <select
                    className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white"
                    value={formData.instructorId}
                    onChange={(e) => {
                      const instructor = instructors.find(i => i.id === e.target.value);
                      setFormData(prev => ({
                        ...prev,
                        instructorId: e.target.value,
                        instructor: instructor ? `${instructor.firstName} ${instructor.lastName}` : ''
                      }));
                    }}
                  >
                    <option value="">-- Seleccionar --</option>
                    {(() => {
                      // Filtrar instructores según el tipo de clase
                      const classTypeId = formData.classType;
                      const classType = classTypes.find(ct => ct.id === classTypeId);

                      // Método 1: instructorIds del tipo de clase
                      const allowedFromClassType = classType?.instructorIds || [];

                      // Método 2: instructores que tienen este tipo de clase en sus classTypeIds
                      const allowedFromInstructor = instructors
                        .filter(inst => inst.classTypeIds?.includes(classTypeId))
                        .map(inst => inst.id);

                      // Combinar ambos métodos
                      const allAllowedIds = [...new Set([...allowedFromClassType, ...allowedFromInstructor])];

                      // Si hay instructores asignados, filtrar; si no, mostrar vacío
                      const filteredInstructors = allAllowedIds.length > 0
                        ? instructors.filter(inst => allAllowedIds.includes(inst.id))
                        : [];

                      return filteredInstructors.map(inst => (
                        <option key={inst.id} value={inst.id}>
                          {inst.firstName} {inst.lastName}
                        </option>
                      ));
                    })()}
                  </select>
                  {(() => {
                    const classTypeId = formData.classType;
                    const classType = classTypes.find(ct => ct.id === classTypeId);
                    const allowedFromClassType = classType?.instructorIds || [];
                    const allowedFromInstructor = instructors
                      .filter(inst => inst.classTypeIds?.includes(classTypeId))
                      .map(inst => inst.id);
                    const allAllowedIds = [...new Set([...allowedFromClassType, ...allowedFromInstructor])];

                    if (allAllowedIds.length === 0 && classTypeId) {
                      return <p className="text-xs text-amber-400 mt-1">⚠️ No hay instructores asignados</p>;
                    }
                    return null;
                  })()}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Hora</label>
                  <select
                    className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white"
                    value={formData.time}
                    onChange={(e) => setFormData(prev => ({ ...prev, time: e.target.value }))}
                  >
                    {TIME_SLOTS.map(time => (
                      <option key={time} value={time}>{time}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Duración</label>
                  <select
                    className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white"
                    value={formData.duration}
                    onChange={(e) => setFormData(prev => ({ ...prev, duration: parseInt(e.target.value) }))}
                  >
                    {DURATION_OPTIONS.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Capacidad Máxima</label>
                <Input
                  type="number"
                  value={formData.maxCapacity}
                  onChange={(e) => setFormData(prev => ({ ...prev, maxCapacity: parseInt(e.target.value) || 0 }))}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Descripción</label>
                <Input
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                />
              </div>

              {/* Lista de alumnos inscritos */}
              <div className="pt-4 border-t border-gray-800">
                <div className="flex items-center justify-between mb-3">
                  <label className="text-sm font-medium text-gray-300 flex items-center gap-2">
                    <Users className="w-4 h-4 text-amber-400" />
                    Alumnos inscritos
                  </label>
                  <span className="text-xs text-gray-500">
                    {getEnrolledCount(selectedClass.enrolled)}/{selectedClass.maxCapacity}
                  </span>
                </div>
                {getEnrolledCount(selectedClass.enrolled) > 0 ? (
                  <div className="bg-gray-800 rounded-lg p-3 max-h-48 overflow-y-auto space-y-2">
                    {getEnrolledUsers(selectedClass.enrolled).map((userId, index) => (
                      <div
                        key={userId}
                        className="flex items-center gap-3 text-sm text-gray-300 p-2 hover:bg-gray-700/50 rounded-lg transition-colors"
                      >
                        <span className="text-xs text-gray-500 w-4">{index + 1}</span>
                        <ProfilePhoto
                          photoUrl={getUserPhoto(userId)}
                          name={getUserName(userId)}
                          size="sm"
                        />
                        <span className="flex-1 truncate">{getUserName(userId)}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="bg-gray-800 rounded-lg p-4 text-center">
                    <Users className="w-8 h-8 text-gray-600 mx-auto mb-2" />
                    <p className="text-sm text-gray-500">No hay alumnos inscritos</p>
                  </div>
                )}
              </div>

              <div className="flex justify-between pt-4 border-t border-gray-800">
                <Button
                  variant="ghost"
                  className="text-red-400 hover:text-red-300 hover:bg-red-400/10"
                  onClick={() => deleteClass(selectedClass.id)}
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Eliminar Clase
                </Button>
                <div className="flex gap-2">
                  <Button variant="ghost" onClick={() => setShowEditModal(false)}>
                    Cancelar
                  </Button>
                  <Button onClick={updateClass}>
                    Guardar Cambios
                  </Button>
                </div>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Clone Week Modal */}
      {showCloneModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <Card className="p-6 w-full max-w-md bg-gray-900 border-gray-800">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-amber-400 flex items-center gap-2">
                <Copy className="w-5 h-5" />
                Clonar Semana
              </h3>
              <Button variant="ghost" size="sm" onClick={() => { setShowCloneModal(false); setCloneWeeksCount(1); }}>
                <X className="w-4 h-4" />
              </Button>
            </div>

            {/* Resumen de la semana origen */}
            <div className="p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg mb-4">
              <p className="text-sm text-blue-300">
                <strong>{classes.length}</strong> clase{classes.length > 1 ? 's' : ''} en la semana del{' '}
                <strong>{new Date(weekDates[0] + 'T12:00:00').toLocaleDateString('es-ES', { day: 'numeric', month: 'short' })}</strong>{' al '}
                <strong>{new Date(weekDates[6] + 'T12:00:00').toLocaleDateString('es-ES', { day: 'numeric', month: 'short' })}</strong>
              </p>
            </div>

            {/* Selector de semanas */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-300 mb-3">¿Cuántas semanas clonar?</label>
              <div className="grid grid-cols-4 gap-2">
                {[1, 2, 3, 4].map(n => (
                  <button
                    key={n}
                    onClick={() => setCloneWeeksCount(n)}
                    className={`py-3 rounded-lg text-sm font-semibold transition-all ${cloneWeeksCount === n
                      ? 'bg-amber-500 text-black'
                      : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                      }`}
                  >
                    {n} sem.
                  </button>
                ))}
              </div>
            </div>

            {/* Preview de semanas destino */}
            <div className="mb-4 space-y-2">
              <label className="block text-xs font-medium text-gray-400">Se clonará a:</label>
              {Array.from({ length: cloneWeeksCount }, (_, i) => {
                const offsetDays = 7 * (i + 1);
                const startDate = new Date(weekDates[0] + 'T12:00:00');
                startDate.setDate(startDate.getDate() + offsetDays);
                const endDate = new Date(weekDates[6] + 'T12:00:00');
                endDate.setDate(endDate.getDate() + offsetDays);
                return (
                  <div key={i} className="flex items-center gap-2 p-2 bg-gray-800 rounded-lg">
                    <Calendar className="w-3.5 h-3.5 text-amber-400 flex-shrink-0" />
                    <span className="text-sm text-gray-300">
                      {startDate.toLocaleDateString('es-ES', { day: 'numeric', month: 'short' })} – {endDate.toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' })}
                    </span>
                  </div>
                );
              })}
            </div>

            {/* Info */}
            <div className="p-3 bg-amber-500/10 border border-amber-500/20 rounded-lg mb-4">
              <p className="text-xs text-amber-300">
                Las clases se crearán como <strong>borrador</strong>. Si ya existe una clase en el mismo horario y sala, se omitirá automáticamente.
              </p>
            </div>

            {/* Acciones */}
            <div className="flex gap-2 pt-2">
              <Button
                variant="ghost"
                className="flex-1"
                onClick={() => { setShowCloneModal(false); setCloneWeeksCount(1); }}
                disabled={isCloning}
              >
                Cancelar
              </Button>
              <Button
                className="flex-1 bg-amber-500 text-black hover:bg-amber-600 font-semibold"
                onClick={cloneWeekToTargets}
                disabled={isCloning || classes.length === 0}
              >
                {isCloning ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Clonando...
                  </>
                ) : (
                  <>
                    <Copy className="w-4 h-4 mr-2" />
                    Clonar {classes.length} clase{classes.length > 1 ? 's' : ''}
                  </>
                )}
              </Button>
            </div>
          </Card>
        </div>
      )}
    </>
  );
};

export default CalendarManagementPage;





